// CartContext.jsx
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart")) || [];
    } catch {
      return [];
    }
  });

  const [promo, setPromo] = useState(() => {
    try { return JSON.parse(localStorage.getItem("promo")) || null; } catch { return null; }
  });

  const [fam26Applied, setFam26Applied] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fam26Applied")) || false; } catch { return false; }
  });

  const [shippingMethod, setShippingMethod] = useState("standard");
  const [province, setProvince] = useState("");

  useEffect(() => { localStorage.setItem("cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("promo", JSON.stringify(promo)); }, [promo]);
  useEffect(() => { localStorage.setItem("fam26Applied", JSON.stringify(fam26Applied)); }, [fam26Applied]);

  // ✅ FIXED: variant-aware cart
  const addToCart = (product, qty = 1) => {
    setItems(prev => {
      const found = prev.find(
        i =>
          i.id === product.id &&
          i.variant?.id === product.variant?.id
      );

      if (found) {
        return prev.map(i =>
          i.id === product.id &&
          i.variant?.id === product.variant?.id
            ? { ...i, qty: i.qty + qty }
            : i
        );
      }

      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          qty,
          image: product.image || product.images?.[0],
          variant: product.variant || null,
        },
      ];
    });
  };

  // ✅ FIXED: remove specific variant
  const removeFromCart = (id, variantId) =>
    setItems(prev =>
      prev.filter(
        i => !(i.id === id && i.variant?.id === variantId)
      )
    );

  // ✅ FIXED: update specific variant quantity
  const setQty = (id, variantId, qty) =>
    setItems(prev =>
      prev.map(i =>
        i.id === id && i.variant?.id === variantId
          ? { ...i, qty: Math.max(1, qty) }
          : i
      )
    );

  const clearCart = () => {
    setItems([]);
    setPromo(null);
    setFam26Applied(false);
  };

  const applyPromo = (code) => {
    const normalized = code.trim().toUpperCase();
    if (normalized === "SAVE10") {
      setPromo({ code: normalized, type: "percent", value: 10 });
      return true;
    }
    if (normalized === "FREESHIP") {
      setPromo({ code: normalized, type: "shipping", value: 100 });
      return true;
    }
    if (normalized === "FAM26") {
      setFam26Applied(true);
      return true;
    }
    return false;
  };

  const removePromo = () => {
    setPromo(null);
    setFam26Applied(false);
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);

    let shipping = items.length ? (shippingMethod === "express" ? 40 : 20) : 0;
    let discount = 0;

    if (promo) {
      if (promo.type === "percent") discount += subtotal * (promo.value / 100);
      if (promo.type === "shipping") shipping = 0;
    }

    if (fam26Applied) discount += subtotal * 0.25;

    const discountedSubtotal = subtotal - discount;

    // Free shipping threshold
    if (discountedSubtotal >= 200) shipping = 0;

    let tax = 0;

    switch (province) {
      case "ON":
        tax = (discountedSubtotal + shipping) * 0.13;
        break;

      case "QC": {
        const gst = (discountedSubtotal + shipping) * 0.05;
        const qst = (discountedSubtotal + shipping + gst) * 0.09975;
        tax = gst + qst;
        break;
      }

      case "BC":
        tax = (discountedSubtotal + shipping) * (0.05 + 0.07);
        break;

      case "AB":
      case "NT":
      case "YT":
      case "NU":
        tax = (discountedSubtotal + shipping) * 0.05;
        break;

      case "MB":
        tax = (discountedSubtotal + shipping) * (0.05 + 0.07);
        break;

      case "SK":
        tax = (discountedSubtotal + shipping) * (0.05 + 0.06);
        break;

      case "NS":
      case "NB":
      case "NL":
      case "PE":
        tax = (discountedSubtotal + shipping) * 0.15;
        break;

      default:
        tax = 0;
    }

    const total = discountedSubtotal + shipping + tax;

    return { subtotal, shipping, discount, tax, total };
  }, [items, promo, fam26Applied, shippingMethod, province]);

  const value = {
    items,
    addToCart,
    removeFromCart,
    setQty,
    clearCart,
    promo,
    applyPromo,
    removePromo,
    fam26Applied,
    setFam26Applied,
    shippingMethod,
    setShippingMethod,
    province,
    setProvince,
    totals,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}